import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";

import { i18n } from "../../translate/i18n";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";

const MENU_TYPES = ["number", "list", "button"];

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  return {
    segmented: {
      display: "flex",
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      overflow: "hidden",
      fontSize: 11.5,
      fontWeight: 600,
    },
    segment: {
      flex: 1,
      padding: "8px 0",
      color: theme.palette.text.secondary,
      background: "transparent",
      border: "none",
      cursor: "pointer",
      font: "inherit",
      fontWeight: 600,
      "& + &": { borderLeft: `1px solid ${borderInput}` },
      "&:hover": { color: theme.palette.text.primary },
    },
    segmentOn: {
      background: "rgba(160,68,255,0.2)",
      color: stateText(theme, "#C08BFF", "purple"),
      "&:hover": { color: stateText(theme, "#C08BFF", "purple") },
    },
    optionsHead: { display: "flex", alignItems: "center", marginBottom: 6 },
    options: { display: "flex", flexDirection: "column", gap: 8 },
    optionRow: { display: "flex", alignItems: "center", gap: 8 },
    badge: {
      width: 18,
      height: 18,
      borderRadius: 5,
      background: "rgba(34,211,238,0.12)",
      color: stateText(theme, AEROS.cyan, "info"),
      fontSize: 10,
      fontWeight: 800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
    },
    removeBtn: {
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 18 },
      "&:hover": { color: AEROS.error },
    },
    // mantém a largura da linha quando só a última opção tem o X
    removeGap: { width: 26, flex: "none" },
  };
});

const FlowBuilderMenuModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [activeModal, setActiveModal] = useState(false);
  const [textDig, setTextDig] = useState();
  const [arrayOption, setArrayOption] = useState([]);
  const [valueRadioButton, setValueRadionButton] = useState("number");

  useEffect(() => {
    if (open === "edit") {
      setTextDig(data.data.message);
      setArrayOption(data.data.arrayOption);
      setValueRadionButton(data.data.typeMenu || "number");
      setActiveModal(true);
    } else if (open === "create") {
      setTextDig();
      setArrayOption([]);
      setValueRadionButton("number");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = async () => {
    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: {
          message: textDig,
          arrayOption: arrayOption,
          typeMenu: valueRadioButton,
        },
      });
      return;
    } else if (open === "create") {
      handleClose();
      onSave({
        message: textDig,
        arrayOption: arrayOption,
        typeMenu: valueRadioButton,
      });
    }
  };

  const removeOption = (number) => {
    setArrayOption((old) => old.filter((item) => item.number !== number));
  };

  const updateOption = (index, value) => {
    setArrayOption((old) => old.map((item, i) => (i === index ? { ...item, value } : item)));
  };

  return (
    <AerosDialog
      open={activeModal}
      onClose={handleClose}
      width={560}
      title={i18n.t(open === "edit" ? "aeros.flow.modals.menu.editTitle" : "aeros.flow.modals.menu.addTitle")}
      onSubmit={handleSaveContact}
      submitLabel={i18n.t(open === "edit" ? "aeros.flow.save" : "aeros.flow.add")}
    >
      <div>
        <label className={m.fieldLabel}>{i18n.t("aeros.flow.modals.menu.type")}</label>
        <div className={classes.segmented} role="tablist">
          {MENU_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={valueRadioButton === t}
              className={`${classes.segment} ${valueRadioButton === t ? classes.segmentOn : ""}`}
              onClick={() => setValueRadionButton(t)}
            >
              {i18n.t(`aeros.flow.modals.menu.${t}`)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={m.fieldLabel} htmlFor="flow-menu-message">
          {i18n.t("aeros.flow.modals.menu.message")}
        </label>
        <TextField
          id="flow-menu-message"
          className={m.input}
          variant="outlined"
          multiline
          rows={4}
          name="text"
          value={textDig || ""}
          placeholder={i18n.t("aeros.flow.content.textPlaceholder")}
          onChange={(e) => setTextDig(e.target.value)}
        />
      </div>
      <div>
        <div className={classes.optionsHead}>
          <label className={m.fieldLabel} style={{ marginBottom: 0 }}>
            {i18n.t("aeros.flow.modals.menu.options")}
          </label>
          <button
            type="button"
            className={m.linkButton}
            onClick={() => setArrayOption((old) => [...old, { number: old.length + 1, value: "" }])}
          >
            <AddIcon />
            {i18n.t("aeros.flow.add")}
          </button>
        </div>
        <div className={classes.options}>
          {arrayOption.map((item, index) => (
            <div className={classes.optionRow} key={item.number}>
              <span className={classes.badge}>{item.number}</span>
              <TextField
                className={m.input}
                variant="outlined"
                placeholder={i18n.t("aeros.flow.modals.menu.optionPlaceholder")}
                value={item.value || ""}
                onChange={(e) => updateOption(index, e.target.value)}
              />
              {arrayOption.length === item.number ? (
                <IconButton
                  className={classes.removeBtn}
                  aria-label={i18n.t("aeros.flow.modals.menu.removeOption")}
                  onClick={() => removeOption(item.number)}
                >
                  <CloseIcon />
                </IconButton>
              ) : (
                <span className={classes.removeGap} />
              )}
            </div>
          ))}
        </div>
      </div>
    </AerosDialog>
  );
};

export default FlowBuilderMenuModal;
